import React, { useEffect, useState, forwardRef, useImperativeHandle } from "react";
import usePortalService from "../../services/PortalService";
import { FileDragDropInputField } from "./FileDragDropInputField";
import { DeletableImage } from "./DeletableImage";
import { Row } from "antd";

type FileData = {
  id: string,
  isOnServer: boolean,
  fileBlob?: Blob
};

type FilesInputProps = {
  filesInit?: string[];
  onChange?: (changed: boolean) => void
}

const FilesInput = forwardRef(({ filesInit, onChange }: FilesInputProps, ref) => {
    const {uploadImages, deleteImages} = usePortalService();

    const [files, setFiles] = useState<FileData[]>([]);
    const [filesToUpload, setFilesToUpload] = useState<FileData[]>([])
    const [filesToDelete, setFilesToDelete] = useState<FileData[]>([])

    const [changed, setChanged] = useState(false);

    useEffect(() => {
      if (filesInit) {
        const initArray = filesInit.map(fileInit => ({
          id: fileInit,
          isOnServer: true,
        }));
        setFiles(initArray);
      }
    }, [filesInit])

    useEffect(() => {
      if (onChange) onChange(changed)
    }, [changed])

    const imagesChangeHandler = (fileList: FileList) => {
      const initArray = Array.from(fileList).map(file => ({
        id: file.name,
        isOnServer: false,
        fileBlob: file,
      }));

      setFilesToUpload(prevFilesToUpload => [...prevFilesToUpload, ...initArray])
      setFiles(prevFiles => [...prevFiles, ...initArray]);

      setChanged(true);
    };

    const deleteFile = (id: string) => {
      const fileToUploadIndex = filesToUpload.findIndex(file => file.id === id);

      if (fileToUploadIndex !== -1) {
        setFilesToUpload(filesToUpload.filter(file => file.id !== id));
      } else {
        setFilesToDelete(prevFilesToDelete => [
          ...prevFilesToDelete,
          files.find(file => file.id === id)!,
        ]);
      }
      setFiles(files.filter(file => file.id !== id));

      setChanged(true);
    }
  
    const uploadHandle = async () => {
      if (!changed) {
        return;
      }
      const updatePromises = []

      if (filesToUpload.length > 0) {
        const data = new FormData();
        filesToUpload.forEach(file => {
          if (file.fileBlob) {
            data.append("Images", file.fileBlob);
          }
        });
        updatePromises.push(uploadImages(2, data));
      }

      if (filesToDelete.length > 0) {
        const imagesIds = filesToDelete.map(item => item.id);
        updatePromises.push(deleteImages({ imagesIds }));
      }
      return Promise.all(updatePromises);
    };

    useImperativeHandle(ref, () => ({ save: uploadHandle }));
    
    return (
      <>
        <FileDragDropInputField handleFile={(file) => imagesChangeHandler(file)} accept="image/png, image/jpeg"/>
        <div style={{paddingTop: '12px'}} >
          <Row justify="start">
            {files.map(file => 
              <DeletableImage 
                key={file.id}
                id={file.id} 
                fileBlob={file.fileBlob}
                type="company" 
                isOnServer={file.isOnServer} 
                deleteHandle={deleteFile}
              />
            )}
          </Row>
        </div>
      </>
    )
})

FilesInput.displayName = "FilesInput";

export default FilesInput;